import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthLayout } from "../../components/AuthLayout";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth-context";

const INTERESTS = ["Amari & bitters", "Fruit liqueurs", "Vermouth", "Nut & spice", "Cream liqueurs", "Coffee & cacao", "Fermented drinks"];
const PATIENCE = ["A weekend", "Two weeks", "A season"];
const GEAR = [
  "Kitchen scale to 1 g",
  "Jars, and somewhere dark to put them",
  "Hydrometer or alcoholmeter",
  "Vacuum sealer or sous-vide bath",
];

export function OnboardingCraft() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [interests, setInterests] = useState<Set<string>>(new Set(["Amari & bitters", "Vermouth"]));
  const [patience, setPatience] = useState("Two weeks");
  const [gear, setGear] = useState<Record<string, boolean>>({
    "Kitchen scale to 1 g": true,
    "Jars, and somewhere dark to put them": true,
    "Hydrometer or alcoholmeter": false,
    "Vacuum sealer or sous-vide bath": false,
  });
  const [busy, setBusy] = useState(false);

  function toggleInterest(label: string) {
    setInterests((prev) => {
      const next = new Set(prev);
      next.has(label) ? next.delete(label) : next.add(label);
      return next;
    });
  }

  async function submit(skip = false) {
    setBusy(true);
    try {
      await api.patch("/me/craft", { craftInterests: Array.from(interests), patience, gear, skip });
      await refresh();
      navigate("/onboarding/shelf");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      posterTitle="Tell us how you like to work."
      posterBody="We'll only show you recipes you can actually finish, on the time and gear you have."
      step={2}
    >
      <span className="eyebrow">Step 2 of 4 · skip any of it</span>
      <h2 style={{ margin: "12px 0 0", fontSize: 38, lineHeight: 1.05, letterSpacing: "-0.028em", marginLeft: "-0.055em" }}>
        What do you make?
      </h2>
      <p className="muted" style={{ margin: "14px 0 28px", fontSize: 14.5, lineHeight: 1.6 }}>
        This only tunes what you see first. Nothing gets hidden from you.
      </p>

      <p className="muted-strong" style={{ margin: "0 0 10px", fontSize: 11, letterSpacing: "0.09em", textTransform: "uppercase" }}>
        Pick what you're into
      </p>
      <div className="wrap-gap" style={{ marginBottom: 28 }}>
        {INTERESTS.map((label) => (
          <button
            key={label}
            type="button"
            onClick={() => toggleInterest(label)}
            className={`tag ${interests.has(label) ? "tag-accent" : "tag-neutral"}`}
            style={{ padding: "8px 14px", fontSize: 13, border: 0, cursor: "pointer" }}
          >
            {label}
          </button>
        ))}
      </div>

      <p className="muted-strong" style={{ margin: "0 0 10px", fontSize: 11, letterSpacing: "0.09em", textTransform: "uppercase" }}>
        How much patience have you got?
      </p>
      <div className="seg" style={{ width: "100%", marginBottom: 28 }}>
        {PATIENCE.map((label) => (
          <label key={label} className="seg-opt" style={{ flex: 1, minHeight: 44, justifyContent: "center" }}>
            <input type="radio" name="patience" checked={patience === label} onChange={() => setPatience(label)} />
            {label}
          </label>
        ))}
      </div>

      <p className="muted-strong" style={{ margin: "0 0 10px", fontSize: 11, letterSpacing: "0.09em", textTransform: "uppercase" }}>
        On the bench
      </p>
      <div className="flex-col" style={{ gap: 10, marginBottom: 30 }}>
        {GEAR.map((label) => (
          <label key={label} className="radio">
            <input type="checkbox" checked={!!gear[label]} onChange={() => setGear((g) => ({ ...g, [label]: !g[label] }))} />
            <span className="dot" />
            {label}
          </label>
        ))}
      </div>

      <div className="flex-row">
        <button className="btn btn-primary" style={{ minHeight: 46 }} disabled={busy} onClick={() => submit(false)}>
          Continue
        </button>
        <button className="btn btn-ghost" disabled={busy} onClick={() => submit(true)}>
          Skip this
        </button>
      </div>
    </AuthLayout>
  );
}
